/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module AviAlertBellModule
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    Output,
} from '@angular/core';

import { AlertCollection, TAlertCollection } from 'ng/modules/collections/factories';
import { L10nService } from '@vmw/ngx-vip';
import { StringService } from 'string-service';
import { IRightRailExpanderConfig } from 'ng/modules/right-rail/components';
import * as moment from 'moment';
import * as globalL10n from 'global-l10n';
import * as l10n from './avi-alert-bell-body.l10n';
import './avi-alert-bell-body.component.less';
import { IAlert, IAlertLevel } from '../../../avi-alert-bell.types';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Extend interface for view alerts base on alert level.
 */
interface IRightRailExpanderAlertConfig extends IRightRailExpanderConfig {
    levelText: string,
}

const ALERT_LEVELS = {
    High: 'ALERT_HIGH',
    Medium: 'ALERT_MEDIUM',
    Low: 'ALERT_LOW',
};

const EVENT_PAGE_INFO = {
    pool: 'EVENT_PAGE_POOL',
    'virtual-service': 'EVENT_PAGE_VS',
};

/**
 * @description Component for rendering avi alert bell body.
 * @author Umesh Chovatiya
 */
@Component({
    selector: 'avi-alert-bell-body',
    templateUrl: './avi-alert-bell-body.component.html',
})
export class AviAlertBellBodyComponent implements AfterViewInit {
    /**
     * Object name for type of alert context.
     */
    @Input()
    public objectName: string;

    @Input()
    public uuid: string;

    /**
     * Alert list with count to display expander base on value.
     */
    @Input()
    public alertLevelList: IAlert;

    /**
     * Emit when alert update or delete.
     */
    @Output()
    public onAlertUpdate = new EventEmitter<IAlertLevel>();

    public readonly eventPages = EVENT_PAGE_INFO;

    public readonly alertCollection: AlertCollection;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Display items base on alert level.
     */
    public readonly alertLevels = ALERT_LEVELS;

    public isLoading = true;

    /**
     * Config for generating the side bar expander list view.
     */
    public expanderListConfig: IRightRailExpanderAlertConfig[];

    constructor(
        public readonly l10nService: L10nService,
        public readonly stringService: StringService,
        @Inject('Timeframe')
        private readonly timeframeService: any,
        @Inject(AlertCollection)
        AlertCollectionClass: TAlertCollection,
    ) {
        this.alertCollection = new AlertCollectionClass();

        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.alertCollection.setParams({
            'event_pages.contains': this.eventPages[this.objectName],
            'related_refs.contains': this.uuid,
            sort: '-timestamp',
        });

        const { step, limit } = this.timeframeService.selected();

        this.alertCollection.loadDuration(step, limit).then(() => {
            this.setExpanderListViewConfig();
        });
    }

    public getPrettyTime(time: number): string {
        return String(moment
            .utc(time)
            .fromNow());
    }

    public deleteAlert(alertId: string): void {
        this.alertCollection.dropItems(alertId).then(() => {
            this.setExpanderListViewConfig();
        });
    }

    /**
     * Set the config for the expander list view based on alert levels.
     * Add items in expander base on alert level is high, medium or low.
     */
    private setExpanderListViewConfig(): void {
        this.expanderListConfig = [];

        const alertListItems = {
            ALERT_HIGH: 0,
            ALERT_MEDIUM: 0,
            ALERT_LOW: 0,
        };

        if (this.alertCollection?.items) {
            this.alertCollection.items.forEach(item => {
                alertListItems[item.getConfig().level]++;
            });
        }

        const highLevel = alertListItems.ALERT_HIGH ?? 0;
        const mediumLevel = alertListItems.ALERT_MEDIUM ?? 0;
        const lowLevel = alertListItems.ALERT_LOW ?? 0;

        if (highLevel > 0) {
            this.expanderListConfig.push({
                icon: 'circle',
                solid: true,
                headerText: `${this.l10nService.getMessage(l10nKeys.highLabel)} (${highLevel})`,
                levelText: this.l10nService.getMessage(l10nKeys.highLabel),
                color: 'avi-alert-bell-body__colors--high',
                hidden: false,
            });
        }

        if (mediumLevel > 0) {
            this.expanderListConfig.push({
                icon: 'circle',
                solid: true,
                headerText: `${this.l10nService.getMessage(l10nKeys.mediumLabel)} (${mediumLevel})`,
                levelText: this.l10nService.getMessage(l10nKeys.mediumLabel),
                color: 'avi-alert-bell-body__colors--medium',
                hidden: false,
            });
        }

        if (lowLevel > 0) {
            this.expanderListConfig.push({
                icon: 'circle',
                solid: true,
                headerText: `${this.l10nService.getMessage(l10nKeys.lowLabel)} (${lowLevel})`,
                levelText: this.l10nService.getMessage(l10nKeys.lowLabel),
                color: 'avi-alert-bell-body__colors--low',
                hidden: false,
            });
        }

        this.onAlertUpdate.emit(alertListItems);
    }

    public trackByIndex(index: number): number {
        return index;
    }
}
